/**
 * Wrap the browser Geolocation API in a Promise with sensible defaults for
 * attendance flows (clock-in / clock-out). We deliberately request
 * `enableHighAccuracy: true` so GPS is preferred over IP geolocation — the
 * owner should know exactly *where* the employee tapped the button.
 *
 * The returned object is always in the shape the backend expects:
 *   { lat, lng, accuracy }  where `accuracy` is metres.
 *
 * On denial / timeout / hardware failure this throws with a human-readable
 * message that can be surfaced directly via toast.error(). Do NOT catch and
 * silently continue — the calling flow (clock-in/out) must abort so the
 * server-side guard doesn't have to.
 */
export async function getCurrentPosition({ timeoutMs = 15000 } = {}) {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    throw new Error(
      "Your device doesn't support Location Services. Open this app in a modern browser."
    );
  }
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords || {};
        if (typeof latitude !== "number" || typeof longitude !== "number") {
          reject(new Error("Could not read your location. Try again."));
          return;
        }
        resolve({
          lat: latitude,
          lng: longitude,
          accuracy: typeof accuracy === "number" ? accuracy : null,
        });
      },
      (err) => {
        // 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        if (err.code === 1) {
          reject(
            new Error(
              "Location permission blocked. Enable Location for this site in your browser settings and try again."
            )
          );
        } else if (err.code === 2) {
          reject(
            new Error(
              "Could not fix your location. Move to an area with a clearer GPS signal and try again."
            )
          );
        } else if (err.code === 3) {
          reject(new Error("Location request timed out. Try once more."));
        } else {
          reject(new Error(err.message || "Could not get your location."));
        }
      },
      {
        enableHighAccuracy: true,
        timeout: timeoutMs,
        maximumAge: 0, // never use a cached position — attendance must be live
      }
    );
  });
}
